/**
 * Smoothly scrolls to the first invalid form field on the page.
 *
 * @param {object} errors - React Hook Form errors object
 * @param {number} [offset=130] - Pixel offset to account for fixed/sticky headers
 */
export const scrollToFirstError = (errors = {}, offset = 130) => {
    // Delay execution slightly so React Hook Form has finished rendering error messages,
    // applying .is-invalid classes, and completing the click event call stack.
    setTimeout(() => {
        const errorKeys = Object.keys(errors || {});
        const candidates = [];

        // 1. Find elements by field name / error key
        for (const key of errorKeys) {
            const el =
                document.getElementById(`field-container-${key}`) ||
                document.querySelector(`[data-field="${key}"]`) ||
                document.getElementById(key) ||
                document.querySelector(`[name="${key}"]`) ||
                document.getElementById(`select-wrapper-${key}`) ||
                document.querySelector(`.${key}`);
            if (el) {
                candidates.push(el);
            }
        }

        // 2. Also find any elements marked .is-invalid that might not have matched the key
        const invalidElements = document.querySelectorAll('.is-invalid');
        invalidElements.forEach((el) => {
            if (!candidates.includes(el)) {
                candidates.push(el);
            }
        });

        // 3. Fallback: find any visible error message (.text-danger)
        if (candidates.length === 0) {
            const dangerElements = document.querySelectorAll('.text-danger');
            dangerElements.forEach((el) => {
                candidates.push(el);
            });
        }

        // Filter for elements currently attached to the DOM
        const connectedCandidates = candidates.filter((el) => el && el.isConnected);
        if (connectedCandidates.length === 0) return;

        // Sort elements by their actual vertical position in the document (topmost first)
        connectedCandidates.sort((a, b) => {
            const scrollY = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
            const topA = a.getBoundingClientRect().top + scrollY;
            const topB = b.getBoundingClientRect().top + scrollY;
            return topA - topB;
        });

        const topElement = connectedCandidates[0];

        // Find the best container element to scroll to (ensures label + input + error are all in view)
        const scrollTarget =
            topElement.closest(
                '.col-12, .col-md-6, .col-sm-12, .col-md-4, .mb-form-group, .mb-2, .mb-3, [class*="col-"], .eformInput'
            ) || topElement;

        const rect = scrollTarget.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
        const targetY = Math.max(0, rect.top + scrollTop - offset);

        try {
            window.scrollTo({
                top: targetY,
                behavior: 'smooth',
            });
        } catch {
            try {
                scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
            } catch {
                window.scrollTo(0, targetY);
            }
        }

        // Focus the input after scrolling starts without interrupting smooth scroll animation
        setTimeout(() => {
            try {
                // If topElement is a container, find the first focusable input inside it
                const focusable =
                    typeof topElement.focus === 'function' && topElement.tagName !== 'DIV'
                        ? topElement
                        : topElement.querySelector?.('input, select, textarea') || topElement;

                if (focusable && typeof focusable.focus === 'function') {
                    focusable.focus({ preventScroll: true });
                }
            } catch {
                // Ignore focus errors
            }
        }, 350);
    }, 80);
};
